import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Badge,
  Progress,
  Alert,
  Spinner,
} from "@/Components/ui";;
import toast, { Toaster } from 'react-hot-toast';

import BreadCrumb from "../../Components/Common/BreadCrumb";
import { useSiteValidation } from "../../hooks/useMessaging";
import { siteService } from "../../api/services/site.service";

const SiteConfiguration = () => {
  const [sites, setSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(true);
  const [validatingStates, setValidatingStates] = useState({});

  const {
    siteStatuses,
    statistics,
    loading,
    error,
    validateSite,
    getSiteStatus,
  } = useSiteValidation();

  // Fetch sites and their status on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await siteService.getSitesApp();
        setSites(response.data.result.sites);
        await getSiteStatus();
      } catch (error) {
        console.error("Failed to fetch data:", error);
        toast.error("데이터를 불러오는데 실패했습니다.");
      } finally {
        setLoadingSites(false);
      }
    };

    fetchData();
  }, [getSiteStatus]);

  const handleValidateSite = async (siteId, autoCreate = false) => {
    setValidatingStates(prev => ({ ...prev, [siteId]: true }));

    try {
      const response = await validateSite({ site_id: siteId, auto_create: autoCreate });
      if (response && response.data) {
        const result = response.data;
        if (result.valid) {
          toast.success(`${result.site_name} 사이트 설정이 유효합니다.`);
        } else {
          toast.error(`${result.site_name} 사이트 설정에 문제가 있습니다: ${result.errors.join(', ')}`);
        }
        // Refresh the status after validation
        await getSiteStatus();
      }
    } catch (error) {
      toast.error("사이트 검증에 실패했습니다.");
    } finally {
      setValidatingStates(prev => ({ ...prev, [siteId]: false }));
    }
  };

  const getStatusBadge = (valid, hasKakaoKey, hasSender) => {
    if (valid) {
      return <Badge color="success">설정완료</Badge>;
    } else if (hasSender && hasKakaoKey) {
      return <Badge color="warning">설정검토필요</Badge>;
    } else if (hasKakaoKey) {
      return <Badge color="info">발신자없음</Badge>;
    } else {
      return <Badge color="danger">설정필요</Badge>;
    }
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return "success";
    if (percentage >= 50) return "warning";
    return "danger";
  };

  const getSiteStatusInfo = (siteId) => {
    if (!siteStatuses || !Array.isArray(siteStatuses)) {
      return {
        valid: false,
        has_kakao_sender_key: false,
        has_sender: false,
        errors: []
      };
    }
    return siteStatuses.find(status => status.site_id === siteId) || {
      valid: false,
      has_kakao_sender_key: false,
      has_sender: false,
      errors: []
    };
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="사이트 설정" pageTitle="메시징" />

          {/* Statistics Overview */}
          <Row className="mb-4">
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">메시징 설정 현황</h5>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md={3}>
                      <div className="text-center">
                        <h4 className="mb-1">{statistics.total_sites}</h4>
                        <p className="text-muted mb-0">전체 사이트</p>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center">
                        <h4 className="mb-1 text-success">{statistics.valid_sites}</h4>
                        <p className="text-muted mb-0">설정완료</p>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center">
                        <h4 className="mb-1 text-info">{statistics.sites_with_kakao_keys}</h4>
                        <p className="text-muted mb-0">카카오 키 설정</p>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center">
                        <h4 className="mb-1">{statistics.setup_completion}</h4>
                        <p className="text-muted mb-0">설정 완료율</p>
                      </div>
                    </Col>
                  </Row>

                  <div className="mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted">전체 설정 진행률</span>
                      <span className="fw-medium">{statistics.setup_completion}</span>
                    </div>
                    <Progress
                      value={parseFloat(statistics.setup_completion)}
                      color={getProgressColor(parseFloat(statistics.setup_completion))}
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Site Configuration List */}
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <Row className="align-items-center">
                    <Col>
                      <h5 className="card-title mb-0">사이트별 메시징 설정</h5>
                    </Col>
                    <Col className="col-auto">
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => getSiteStatus()}
                        disabled={loading}
                      >
                        {loading ? (
                          <Spinner size="sm" className="me-1" />
                        ) : (
                          <i className="ri-refresh-line align-bottom me-1"></i>
                        )}
                        새로고침
                      </Button>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody>
                  {error && (
                    <Alert color="danger">
                      <strong>오류:</strong> {error}
                    </Alert>
                  )}

                  {loadingSites || loading ? (
                    <div className="text-center py-4">
                      <Spinner color="primary" />
                      <p className="text-muted mt-2">데이터를 불러오는 중...</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>사이트명</th>
                            <th>서브도메인</th>
                            <th>상태</th>
                            <th>카카오 키</th>
                            <th>발신자</th>
                            <th>오류</th>
                            <th>작업</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(sites || []).map(site => {
                            const statusInfo = getSiteStatusInfo(site.id);
                            const isValidating = validatingStates[site.id];

                            return (
                              <tr key={site.id}>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <div>
                                      <h6 className="mb-0">{site.name}</h6>
                                      <small className="text-muted">ID: {site.id}</small>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <code>{site.subdomain || '-'}</code>
                                </td>
                                <td>
                                  {getStatusBadge(
                                    statusInfo.valid,
                                    statusInfo.has_kakao_sender_key,
                                    statusInfo.has_sender
                                  )}
                                </td>
                                <td>
                                  {statusInfo.has_kakao_sender_key ? (
                                    <Badge color="success">
                                      <i className="ri-check-line"></i> 설정됨
                                    </Badge>
                                  ) : (
                                    <Badge color="danger">
                                      <i className="ri-close-line"></i> 없음
                                    </Badge>
                                  )}
                                </td>
                                <td>
                                  {statusInfo.has_sender ? (
                                    <Badge color="success">
                                      <i className="ri-check-line"></i> 있음
                                    </Badge>
                                  ) : (
                                    <Badge color="warning">
                                      <i className="ri-close-line"></i> 없음
                                    </Badge>
                                  )}
                                </td>
                                <td>
                                  {statusInfo.errors && statusInfo.errors.length > 0 ? (
                                    <div className="text-danger small">
                                      {statusInfo.errors.slice(0, 2).map((error, index) => (
                                        <div key={index}>{error}</div>
                                      ))}
                                      {statusInfo.errors.length > 2 && (
                                        <div>...외 {statusInfo.errors.length - 2}개</div>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                                <td>
                                  <div className="d-flex gap-1">
                                    <Button
                                      color="outline-primary"
                                      size="sm"
                                      onClick={() => handleValidateSite(site.id, false)}
                                      disabled={isValidating}
                                    >
                                      {isValidating ? (
                                        <Spinner size="sm" />
                                      ) : (
                                        <i className="ri-search-line"></i>
                                      )}
                                    </Button>
                                    {!statusInfo.valid && (
                                      <Button
                                        color="outline-success"
                                        size="sm"
                                        onClick={() => handleValidateSite(site.id, true)}
                                        disabled={isValidating}
                                        title="자동 설정 시도"
                                      >
                                        {isValidating ? (
                                          <Spinner size="sm" />
                                        ) : (
                                          <i className="ri-settings-line"></i>
                                        )}
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Help Information */}
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">설정 도움말</h5>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md={6}>
                      <h6 className="text-muted">설정 상태 설명</h6>
                      <ul className="list-unstyled">
                        <li className="mb-2">
                          <Badge color="success" className="me-2">설정완료</Badge>
                          모든 설정이 완료되어 메시지 발송 가능
                        </li>
                        <li className="mb-2">
                          <Badge color="warning" className="me-2">설정검토필요</Badge>
                          설정은 있으나 검증 필요
                        </li>
                        <li className="mb-2">
                          <Badge color="info" className="me-2">발신자없음</Badge>
                          카카오 키는 있으나 발신자 프로필 없음
                        </li>
                        <li className="mb-2">
                          <Badge color="danger" className="me-2">설정필요</Badge>
                          카카오 키 설정부터 필요
                        </li>
                      </ul>
                    </Col>
                    <Col md={6}>
                      <h6 className="text-muted">작업 버튼 설명</h6>
                      <ul className="list-unstyled">
                        <li className="mb-2">
                          <Button color="outline-primary" size="sm" className="me-2">
                            <i className="ri-search-line"></i>
                          </Button>
                          설정 상태 검증
                        </li>
                        <li className="mb-2">
                          <Button color="outline-success" size="sm" className="me-2">
                            <i className="ri-settings-line"></i>
                          </Button>
                          자동 설정 시도 (문제 있는 사이트만)
                        </li>
                      </ul>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <Toaster position="top-center" />
    </React.Fragment>
  );
};

export default SiteConfiguration;